import { HttpErrorResponse } from '@angular/common/http';

export interface ParsedApiError {
  /** camelCase field name -> messages (per-field validation errors) */
  fieldErrors: Record<string, string[]>;
  /** nonFieldErrors + detail + bare-array body + string body */
  nonFieldMessages: string[];
  status: number | null;
}

const STATUS_FALLBACKS: Record<number, string> = {
  0: 'A network or CORS error occurred. Please check your connection or try again.',
  400: 'Invalid input data',
  401: 'Please log in to continue',
  403: "You don't have permission to perform this action",
  404: 'The requested resource was not found',
  500: 'A server error occurred. Please try again later',
};

const GENERIC_MESSAGE = 'An error occurred while processing your request';

function toMessages(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (value != null && typeof value === 'object') {
    // Defensive: flatten a nested object's values into messages.
    return Object.values(value).flatMap((v) => toMessages(v));
  }
  return [String(value)];
}

/**
 * Single source of truth for turning a DRF error response into a normalized
 * shape. Pure: empty messages means "no server message" — the status-code
 * fallback lives only in {@link getErrorMessage}.
 */
export function parseApiError(
  error: HttpErrorResponse | null | undefined,
): ParsedApiError {
  const result: ParsedApiError = {
    fieldErrors: {},
    nonFieldMessages: [],
    status: error?.status ?? null,
  };

  if (error == null || error.status === 0) return result;

  const body = error.error;

  // Do not attempt to parse client-side JS/browser errors or network/CORS event objects.
  if (
    body != null &&
    (body instanceof Error ||
      (typeof Event !== 'undefined' && body instanceof Event) ||
      body.constructor?.name === 'ProgressEvent' ||
      body.constructor?.name === 'ErrorEvent')
  ) {
    return result;
  }

  // Order matters: arrays are typeof 'object', so handle them first. This is
  // the DRF bare-string ValidationError shape: ["this code does not exist"].
  if (Array.isArray(body)) {
    result.nonFieldMessages = toMessages(body);
    return result;
  }

  if (typeof body === 'string') {
    result.nonFieldMessages = body ? [body] : [];
    return result;
  }

  if (body != null && typeof body === 'object') {
    for (const [key, value] of Object.entries(body)) {
      if (key === 'nonFieldErrors') {
        result.nonFieldMessages.push(...toMessages(value));
      } else if (key === 'detail') {
        result.nonFieldMessages.push(String(value));
      } else {
        result.fieldErrors[key] = toMessages(value);
      }
    }
  }

  return result;
}

/**
 * Flat human-readable message for toast notifications. Built on top of
 * {@link parseApiError} so there is one parser. Preserves the historical
 * output wording (field errors rendered as "field: msgs", status fallbacks).
 */
export function getErrorMessage(
  error: HttpErrorResponse | null | undefined,
): string {
  if (error == null) return GENERIC_MESSAGE;

  const { fieldErrors, nonFieldMessages } = parseApiError(error);

  // General messages win and join with ', '. For a flat toast we show just the
  // backend messages (which are already self-descriptive) without the field-key
  // prefix — field association belongs inline on the form, not in the toast.
  if (nonFieldMessages.length) return nonFieldMessages.join(', ');

  const fieldMessages = Object.values(fieldErrors).flat();
  if (fieldMessages.length) return fieldMessages.join(' ');

  // Prioritize friendly status-based fallbacks (like network/CORS error or server errors)
  // over generic/technical error message strings.
  const fallback =
    error.status !== null ? STATUS_FALLBACKS[error.status] : undefined;
  if (fallback) return fallback;

  // No message from the body or friendly fallback — fall back to Angular's message.
  if (error.message) return error.message;
  return GENERIC_MESSAGE;
}

/**
 * Bulk endpoints return `{ "<id>": { errors: {...} } | { nonFieldErrors: [...] } }`.
 * Opt-in only: a bulk body is indistinguishable from a field-validation object
 * with numeric-string keys, so callers must invoke this explicitly.
 */
export function parseBulkApiError(
  error: HttpErrorResponse | null | undefined,
): Record<string, ParsedApiError> {
  const out: Record<string, ParsedApiError> = {};
  const body = error?.error;
  if (body == null || typeof body !== 'object' || Array.isArray(body))
    return out;

  for (const [id, value] of Object.entries(body)) {
    // Each entry may wrap its field errors under an `errors` key.
    const inner =
      value != null &&
      typeof value === 'object' &&
      'errors' in (value as object)
        ? (value as { errors: unknown }).errors
        : value;
    out[id] = parseApiError({
      status: error?.status ?? null,
      error: inner,
    } as unknown as HttpErrorResponse);
  }

  return out;
}
